// What the seed forecast is, and how it is allowed to be described.
//
// The seed is the deep harness's first pass — one cheap frontier call that
// produces a provisional number before any research runs. It reaches us as an
// ordinary `tool_call_started`/`tool_call_finished` pair, distinguished only by
// `call_id: "seed"` and `detail.seed`.
//
// Its producer-supplied `title` names the model it used ("Seed forecast ·
// google/gemini-3.1-pro-preview"). That is an implementation detail of ours and
// must never reach a rendered surface, so nothing here derives display text
// from `title` — the phrasing below is fixed on this side of the wire.

/** The chat beat announcing that the seed has launched. */
export const SEED_STARTED_TEXT = "Making an initial forecast…";

/** The chat beat for a seed that returned nothing (the seedless fallback). */
export const SEED_FAILED_TEXT = "No initial forecast — going straight to research.";

// The two predicates below are deliberately DIFFERENT widths, because being
// wrong costs something different in each place.
//
//   - Identity (`isSeedCall`) decides whether to write seed state, which is
//     what produces a chat message. A false positive INVENTS a message about a
//     forecast that never happened, so it matches the contract exactly.
//   - The leak guard (`mayBeSeedCall`) decides whether to withhold a title. A
//     false positive costs one status line; a false NEGATIVE leaks a model
//     name, which is the thing we are preventing. So it is deliberately wider.
//
// Narrowing the identity is also what keeps this consistent with the seed
// bookkeeping already in the reducer, which keys on `call_id` alone (see the
// "does not label a forecast as the seed estimate after another tool starts"
// regression test).

/**
 * Whether a tool call IS the seed. The contract fixes `call_id` to `"seed"`
 * (S5), and nothing else is accepted — see the note above.
 */
export function isSeedCall(callId: string | null): boolean {
  return callId === "seed";
}

/**
 * Whether a tool call MIGHT be the seed, and so must not show its own title.
 *
 * Wider than {@link isSeedCall} on purpose: `detail.seed` is what the producer
 * sets on every frame of the pair, so a renamed call still cannot leak.
 */
export function mayBeSeedCall(callId: string | null, detail: Record<string, unknown>): boolean {
  return isSeedCall(callId) || detail.seed === true;
}
